import { ref, computed, onMounted, onUnmounted } from 'vue';
import { t } from '@/utils/i18n-helper';
import { api } from '@/api.js';
import { useToast } from '@/composables/common/useToast';
import informacionPagoService from '@/services/perfil/informacionPago';

const IFRAME_TIMEOUT_MS = 120000;
const POLL_INTERVAL_MS = 3000;

export function useInformacionPago({ admin = false, userId, tid: tidProp } = {}) {
    const loading = ref(false);
    const tarjeta = ref(null);
    const modoEdicion = ref(false);
    const jetId = ref('');
    const iframe = ref('');
    const errorMsg = ref('');
    const procesando = ref(false);
    const pagoEnProceso = ref(false);
    const mostrarConfirmacion = ref(false);
    const { toast } = useToast();
    let iframePollInterval = null;
    let iframeTimeout = null;
    let previousCardFingerprint = null;

    const infoEncontrada = computed(() => !!tarjeta.value);

    const getTid = () => tidProp;

    const stopIframeListener = () => {
        if (iframePollInterval) {
            clearInterval(iframePollInterval);
            iframePollInterval = null;
        }
        if (iframeTimeout) {
            clearTimeout(iframeTimeout);
            iframeTimeout = null;
        }
    };

    const getCardFingerprint = card => {
        if (!card) return null;
        return card.panVuePayCard + '|' + card.mesCaducidad + '|' + card.anoCaducidad;
    };

    const fetchPaymentInfo = async() => {
        const tid = getTid();
        const res = await informacionPagoService.getInfoPago({ tid, userId, admin });
        if (res.data?.titular && res.data?.panVuePayCard) {
            return {
                titular: res.data.titular,
                panVuePayCard: res.data.panVuePayCard,
                mesCaducidad: res.data.mesCaducidad,
                anoCaducidad: res.data.anoCaducidad,
            };
        }
        return null;
    };

    // Polling variant that avoids Vuex dispatch entirely — prevents any
    // potential reactivity cascade during the 3D Secure iframe flow.
    const pollPaymentInfo = async() => {
        const tid = getTid();
        const res = await api.call(
            config.getAPIRoute('front.perfil.info_pago'),
            { tid, filters: {} }
        );
        if (res.data?.titular && res.data?.panVuePayCard) {
            return {
                titular: res.data.titular,
                panVuePayCard: res.data.panVuePayCard,
                mesCaducidad: res.data.mesCaducidad,
                anoCaducidad: res.data.anoCaducidad,
            };
        }
        return null;
    };

    const cargarInfo = async() => {
        loading.value = true;
        try {
            const card = await fetchPaymentInfo();
            tarjeta.value = card;
        } catch (e) {
            errorMsg.value = t('perfil.error');
            if (process.env.NODE_ENV !== 'production') {
                console.error('Error al cargar info de pago');
            }
        } finally {
            loading.value = false;
        }
    };

    const confirmarEdicion = async() => {
        mostrarConfirmacion.value = false;
        try {
            modoEdicion.value = true;
            const tid = getTid();
            const res = await informacionPagoService.getJetId({ tid, userId, admin });
            jetId.value = res.data.jet_id;
        } catch (e) {
            errorMsg.value = t('perfil.error');
            if (process.env.NODE_ENV !== 'production') {
                console.error('Error al obtener jet_id');
            }
        }
    };

    const activarEdicion = () => {
        pagoEnProceso.value = false;
        if (infoEncontrada.value) {
            mostrarConfirmacion.value = true;
        } else {
            return confirmarEdicion();
        }
    };

    const cancelarEdicion = () => {
        stopIframeListener();
        modoEdicion.value = false;
        mostrarConfirmacion.value = false;
        errorMsg.value = '';
        iframe.value = '';
        pagoEnProceso.value = false;
    };

    const onIframeComplete = () => {
        stopIframeListener();
        iframe.value = '';
        pagoEnProceso.value = false;
        cargarInfo().then(() => {
            modoEdicion.value = false;
            toast.success(t('perfil.info_pago_actualizada'));
        });
    };

    const startIframeListener = () => {
        stopIframeListener();

        const iframeUrl = iframe.value;
        if (!iframeUrl) return;

        previousCardFingerprint = getCardFingerprint(tarjeta.value);

        const doPoll = async() => {
            try {
                const card = await pollPaymentInfo();
                const fingerprint = getCardFingerprint(card);

                if (fingerprint !== previousCardFingerprint) {
                    tarjeta.value = card;
                    onIframeComplete();
                }
            } catch {
                // polling errors are non-fatal, keep trying
            }
        };

        iframePollInterval = setInterval(doPoll, POLL_INTERVAL_MS);

        iframeTimeout = setTimeout(() => {
            stopIframeListener();
            iframe.value = '';
            pagoEnProceso.value = false;
            errorMsg.value = t('perfil.error_verificacion_tarjeta', {}, 'La verificación tardó demasiado. Revisa tu email o contacta con soporte.');
        }, IFRAME_TIMEOUT_MS);
    };

    const procesarConfirmacionTarjeta = async jetToken => {
        errorMsg.value = '';
        procesando.value = true;
        pagoEnProceso.value = true;
        try {
            const tid = getTid();
            const res = await informacionPagoService.procesarConfirmacionTarjeta({ tid, jetToken, userId, admin });
            const response = res.data.response;
            if (response.challengeUrl) {
                iframe.value = response.challengeUrl;
                startIframeListener();
            } else if (response.compraFinalizada) {
                modoEdicion.value = false;
                pagoEnProceso.value = false;
                await cargarInfo();
                toast.success(t('perfil.info_pago_actualizada'));
            } else {
                pagoEnProceso.value = false;
            }
        } catch (e) {
            errorMsg.value = t('perfil.error');
            pagoEnProceso.value = false;
        } finally {
            procesando.value = false;
        }
    };

    onMounted(async() => {
        await cargarInfo();
        if (!infoEncontrada.value) {
            await activarEdicion();
        }
    });

    onUnmounted(() => {
        stopIframeListener();
    });

    return {
        loading,
        tarjeta,
        infoEncontrada,
        modoEdicion,
        mostrarConfirmacion,
        jetId,
        iframe,
        errorMsg,
        procesando,
        pagoEnProceso,
        cargarInfo,
        activarEdicion,
        confirmarEdicion,
        cancelarEdicion,
        procesarConfirmacionTarjeta,
    };
}
